/**
 * Order Confirmation Page
 *
 * Displays order confirmation after successful checkout.
 * Accessible at /s/:storeSlug/order/:orderNumber
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection, type LanguageCode } from '../../i18n/languages';
import StoreFooter from '../../components/storefront/StoreFooter';
import CookieConsent from '../../components/storefront/CookieConsent';

interface Store {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  theme: StoreTheme;
  currency: string;
  locale: LanguageCode;
  contact_email?: string;
}

interface StoreTheme {
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  payment_status: string;
  customer_email: string;
  customer_name: string;
  subtotal: number;
  total: number;
  currency: string;
  items: OrderItem[];
  shipping_address?: ShippingAddress;
  created_at: string;
}

interface OrderItem {
  product_name: string;
  variant_name?: string;
  unit_price: number;
  quantity: number;
  total_price: number;
}

interface ShippingAddress {
  address1: string;
  address2?: string;
  city: string;
  state?: string;
  postal_code: string;
  country: string;
}

const defaultTheme: StoreTheme = {
  primaryColor: '#0d9488',
  secondaryColor: '#6366f1',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
};

export default function OrderConfirmationPage() {
  const { storeSlug, orderNumber } = useParams<{ storeSlug: string; orderNumber: string }>();
  const { t, i18n } = useTranslation('store');
  const [store, setStore] = useState<Store | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadOrder() {
      try {
        setLoading(true);

        // Load store data
        const storeResponse = await fetch(`/api/public/stores/${storeSlug}`);
        if (!storeResponse.ok) {
          setError('Store not found');
          return;
        }

        const storeData = await storeResponse.json() as { store: Store };
        setStore(storeData.store);

        // Load order data
        const orderResponse = await fetch(`/api/public/stores/${storeSlug}/orders/${orderNumber}`);
        if (!orderResponse.ok) {
          setError('Order not found');
          return;
        }

        const orderData = await orderResponse.json() as { order: Order };
        setOrder(orderData.order);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load order');
      } finally {
        setLoading(false);
      }
    }

    if (storeSlug && orderNumber) {
      loadOrder();
    }
  }, [storeSlug, orderNumber]);

  const theme = store?.theme || defaultTheme;

  const storeLocale = store?.locale || 'en';
  const storeDirection = getLanguageDirection(storeLocale);

  // Change language when store locale is set
  useEffect(() => {
    if (store?.locale && store.locale !== i18n.language) {
      i18n.changeLanguage(store.locale);
    }
  }, [store?.locale, i18n]);

  const localeMap: Record<string, string> = {
    en: 'en-US', zh: 'zh-CN', hi: 'hi-IN', es: 'es-ES', fr: 'fr-FR',
    ar: 'ar-SA', bn: 'bn-BD', pt: 'pt-BR', ru: 'ru-RU', ja: 'ja-JP',
    de: 'de-DE', ko: 'ko-KR', vi: 'vi-VN', tr: 'tr-TR', it: 'it-IT',
    th: 'th-TH', pl: 'pl-PL', nl: 'nl-NL', id: 'id-ID', he: 'he-IL',
  };
  const intlLocale = localeMap[storeLocale] || 'en-US';

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(intlLocale, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(intlLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {error || t('errors.somethingWentWrong')}
          </h1>
          <p className="text-gray-500 mb-6">{t('order.unableToLoad', 'Unable to load order details.')}</p>
          <Link to={`/s/${storeSlug}`} className="text-teal-600 hover:text-teal-700 font-medium">
            {t('order.returnToStore', 'Return to Store')}
          </Link>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('order.orderNotFound')}</h1>
          <p className="text-gray-500 mb-6">{t('order.orderNotFoundDescription')}</p>
          <Link to={`/s/${storeSlug}`} className="text-teal-600 hover:text-teal-700 font-medium">
            {t('order.returnToStore', 'Return to Store')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={storeDirection} lang={storeLocale}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to={`/s/${store.slug}`} className="flex items-center gap-3">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="h-10 w-auto" />
              ) : (
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  {store.name.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-xl font-bold">{store.name}</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: `${theme.primaryColor}20` }}
          >
            <svg
              className="w-10 h-10"
              style={{ color: theme.primaryColor }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('order.thankYou')}</h1>
          <p className="text-gray-500">
            {t('order.orderNumber')} #{order.order_number}
          </p>
          <p className="text-sm text-gray-400 mt-2">
            {t('order.confirmationSent', 'A confirmation email has been sent to')} {order.customer_email}
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Order Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t('order.orderDetails')}</h2>
                <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
              </div>
              <span
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  backgroundColor: `${theme.primaryColor}20`,
                  color: theme.primaryColor,
                }}
              >
                {t(`order.${order.status}`, order.status.charAt(0).toUpperCase() + order.status.slice(1))}
              </span>
            </div>
          </div>

          {/* Items */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-4">{t('order.items')}</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-900">{item.product_name}</p>
                    {item.variant_name && (
                      <p className="text-xs text-gray-500">{item.variant_name}</p>
                    )}
                    <p className="text-xs text-gray-400">{t('product.quantity')}: {item.quantity}</p>
                  </div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatPrice(item.total_price, order.currency)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Shipping Address */}
          {order.shipping_address && (
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">{t('order.shippingAddress')}</h3>
              <div className="text-sm text-gray-600">
                <p>{order.customer_name}</p>
                <p>{order.shipping_address.address1}</p>
                {order.shipping_address.address2 && <p>{order.shipping_address.address2}</p>}
                <p>
                  {order.shipping_address.city}
                  {order.shipping_address.state && `, ${order.shipping_address.state}`}{' '}
                  {order.shipping_address.postal_code}
                </p>
                <p>{order.shipping_address.country}</p>
              </div>
            </div>
          )}

          {/* Payment */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900 mb-2">{t('checkout.paymentMethod')}</h3>
            <p className="text-sm text-gray-600">
              {order.payment_status === 'pending' ? t('checkout.payOnDelivery') : t(`order.${order.payment_status}`, order.payment_status)}
            </p>
          </div>

          {/* Totals */}
          <div className="px-6 py-4 bg-gray-50">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('order.subtotal')}</span>
                <span className="text-gray-900">{formatPrice(order.subtotal, order.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('order.shipping')}</span>
                <span className="text-gray-900">{t('order.free')}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-gray-200">
                <span>{t('order.total')}</span>
                <span style={{ color: theme.primaryColor }}>
                  {formatPrice(order.total, order.currency)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={`/s/${store.slug}`}
            className="px-6 py-3 rounded-lg text-white font-medium text-center"
            style={{ backgroundColor: theme.primaryColor }}
          >
            {t('order.continueShopping')}
          </Link>
          {store.contact_email && (
            <a
              href={`mailto:${store.contact_email}?subject=Question about order ${order.order_number}`}
              className="px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium text-center hover:bg-gray-50"
            >
              {t('order.contactStore', 'Contact Store')}
            </a>
          )}
        </div>

        {/* Next Steps */}
        <div className="mt-12 bg-blue-50 rounded-xl p-6">
          <h3 className="text-sm font-medium text-blue-900 mb-3">{t('order.whatsNext', 'What happens next?')}</h3>
          <ul className="text-sm text-blue-700 space-y-2">
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>{t('order.nextStep1', "You'll receive an email confirmation with your order details")}</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              <span>{t('order.nextStep2', 'The store will prepare and ship your order')}</span>
            </li>
            <li className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{t('order.nextStep3', "You'll receive tracking information once shipped")}</span>
            </li>
          </ul>
        </div>
      </main>

      {/* Footer */}
      <StoreFooter store={store} direction={storeDirection} />

      {/* Cookie Consent */}
      <CookieConsent
        storeId={store.id}
        storeSlug={store.slug}
        primaryColor={theme.primaryColor}
      />
    </div>
  );
}
